import { useEffect, useRef, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2, ShieldCheck, AlertTriangle, FileText, FileDown, Upload, Trash2, FileSpreadsheet, CalendarRange, Printer, Archive, Image as ImageIcon, Check, X, ChevronRight, ChevronDown, Pencil, Move, Eye, FolderOpen, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDateTime } from "@/lib/dates";
import { toast } from "sonner";

export default function Compliance() {
  const [summary, setSummary] = useState(null);
  const [forms, setForms] = useState([]);
  // iter-62: never leave the loader spinning forever if one of the two
  // initial calls fails (the production bug the user reported on 2026-02-19).
  const [loadError, setLoadError] = useState("");

  const loadInitial = () => {
    setLoadError("");
    setSummary(null);
    Promise.all([
      api.get("/compliance/summary").then((r) => r.data),
      api.get("/forms/library").then((r) => r.data),
    ])
      .then(([s, f]) => { setSummary(s); setForms(f); })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("compliance load failed", err);
        setLoadError(formatApiError(err) || "Could not load the compliance overview.");
        // Render the page with safe defaults so the user can still navigate.
        // Include every field the JSX reads so we never throw a 'reading length
        // of undefined' (iter-64 fix — was crashing on summary.ratios.length).
        setSummary({
          percent_complete: 0,
          jfs_01234_signed: 0,
          jfs_01234_missing: 0,
          total_children: 0,
          ratios: [],
          sections: [],
          children_with_gaps: [],
          expirations_30_days: [],
        });
        setForms([]);
      });
  };

  useEffect(() => { loadInitial(); }, []);

  const openBlankForm = async (code) => {
    try {
      const res = await api.get(`/forms/${code}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openWeeklyAttendance = async () => {
    try {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((today.getDay() || 7) - 1));
      const week_of = monday.toISOString().slice(0, 10);
      const res = await api.get(`/attendance/weekly/pdf?week_of=${week_of}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!summary) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="compliance-page" className="space-y-10">
      {loadError && (
        <div data-testid="compliance-load-error" className="rounded-2xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900 text-sm">Couldn't load the latest compliance overview</div>
            <div className="text-xs text-amber-800 mt-0.5 leading-relaxed">{loadError}<br/>Showing a blank shell so the rest of the page still works — your data is safe.</div>
          </div>
          <Button onClick={loadInitial} size="sm" variant="outline" className="rounded-lg h-8 text-xs">Retry</Button>
        </div>
      )}
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">DCY compliance</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Compliance overview</h1>
          <p className="mt-2 text-stone-500">How ready your center is for a licensing visit today.</p>
        </div>
        <MonthlyDigestActions />
      </header>

      <SettingsHintBanner />

      <section className="rounded-3xl border border-stone-200 bg-white p-8">
        <div className="flex items-center gap-5">
          <div className="relative w-28 h-28 rounded-full grid place-items-center" style={{
            background: `conic-gradient(#047857 ${(summary.percent_complete / 100) * 360}deg, #e7e5e4 0deg)`,
          }}>
            <div className="absolute inset-2 rounded-full bg-white grid place-items-center">
              <div className="text-center">
                <div className="font-display text-3xl font-medium text-stone-900">{summary.percent_complete}%</div>
                <div className="text-[9px] uppercase tracking-widest text-stone-500">Audit ready</div>
              </div>
            </div>
          </div>
          <div>
            <div className="font-display text-2xl font-medium text-stone-900">DCY 01234 status</div>
            <div className="mt-2 text-sm text-stone-600">
              <span className="text-emerald-700 font-semibold">{summary.jfs_01234_signed}</span> signed ·
              <span className="ml-2 text-orange-700 font-semibold">{summary.jfs_01234_missing}</span> missing · {summary.total_children} total children
            </div>
            {summary.jfs_01234_missing > 0 && (
              <Link to="/dashboard/children" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                Resolve missing forms →
              </Link>
            )}
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-medium text-stone-900">OAC 5101:2-12-18 · Ratio audit</h2>
        <div className="mt-4 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="compliance-ratio-table">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-[11px] uppercase tracking-widest text-stone-500">
                <th className="text-left font-semibold px-5 py-3">Age group</th>
                <th className="text-left font-semibold px-5 py-3">Required ratio</th>
                <th className="text-right font-semibold px-5 py-3">Children</th>
                <th className="text-right font-semibold px-5 py-3">Staff needed</th>
              </tr>
            </thead>
            <tbody>
              {(summary.ratios?.length ?? 0) === 0 ? (
                <tr><td colSpan="4" className="px-5 py-12 text-center text-stone-500 text-sm">No children enrolled yet.</td></tr>
              ) : summary.ratios.map((r) => (
                <tr key={r.age_group} className="border-b border-stone-100 last:border-0">
                  <td className="px-5 py-4 text-sm text-stone-900 font-medium">{r.label}</td>
                  <td className="px-5 py-4 text-sm text-stone-700">{r.required_ratio}</td>
                  <td className="px-5 py-4 text-right font-display font-semibold text-stone-900">{r.children}</td>
                  <td className="px-5 py-4 text-right font-display font-semibold text-emerald-700">{r.staff_needed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-medium text-stone-900">Ohio required forms — click to open the official PDF</h2>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="compliance-forms">
          {forms.map((f) => (
            <button
              key={f.code}
              data-testid={`compliance-form-${f.code}`}
              onClick={() => openBlankForm(f.code)}
              className="text-left rounded-xl border border-stone-200 bg-white p-4 hover:border-emerald-400 hover:shadow-soft hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between">
                <FileText className="w-4 h-4 text-stone-400" />
                <FileDown className="w-4 h-4 text-emerald-700" />
              </div>
              <div className="mt-3 font-display text-sm font-semibold text-stone-900">{f.code.toUpperCase().replace("JFS_", "DCY_")}</div>
              <div className="mt-1 text-[11px] text-stone-500 leading-snug">{f.title.replace(/^.*?— /, "")}</div>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <Button
            data-testid="compliance-weekly-attendance-btn"
            onClick={openWeeklyAttendance}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
          >
            <FileDown className="w-4 h-4 mr-2" /> Generate this week's DCY 01208 attendance sheet
          </Button>
        </div>
      </section>

      <CaseClosureTrackerArchivedMarker />
    </div>
  );
}

function CaseClosureTrackerArchivedMarker() {
  // Compliance page no longer renders Cases / Attendance archive — those
  // live in the unlocked Records section. We render nothing here on
  // purpose; the previous prompt-card was removed at the user's request.
  return null;
}


// =============== Case Closure Tracker ===============
// Provider uploads an .xlsx of subsidy cases with closure dates. The system
// flags families ≤30 days from closure (yellow) and after closure (red).
// Cases are auto-purged 90 days after closure.

export function CaseClosureTracker() {
  const [data, setData] = useState({ cases: [], summary: { active: 0, expiring: 0, expired: 0, closed: 0 } });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/compliance/cases")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/compliance/cases/template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "case-tracking-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(xlsx|csv)$/)) {
      toast.error("Please upload an .xlsx or .csv file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/compliance/cases/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { inserted, updated, skipped, errors } = r.data || {};
      toast.success(`Imported ${inserted} new · ${updated} updated${skipped ? ` · ${skipped} skipped` : ""}`);
      if (errors?.length) toast.warning(errors.join("\n"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Remove this case from tracking?")) return;
    try {
      await api.delete(`/compliance/cases/${id}`);
      toast.success("Case removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCaseState = async (c, nextState) => {
    setToggling(c.id);
    try {
      await api.patch(`/compliance/cases/${c.id}`, { state: nextState });
      toast.success(nextState === "closed" ? `${c.family_name} marked closed` : `${c.family_name} reopened`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setToggling(null); }
  };

  const [emailEditing, setEmailEditing] = useState(null); // { id, value }
  const saveEmail = async () => {
    if (!emailEditing) return;
    try {
      await api.patch(`/compliance/cases/${emailEditing.id}`, { parent_email: emailEditing.value.trim() });
      toast.success(emailEditing.value.trim() ? "Parent email saved" : "Parent email cleared");
      setEmailEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [notifying, setNotifying] = useState(null);
  const notifyFamily = async (c) => {
    if (!window.confirm(`Send a courtesy reminder to ${c.parent_email}?`)) return;
    setNotifying(c.id);
    try {
      const r = await api.post(`/compliance/cases/${c.id}/notify`);
      toast.success(`Reminder emailed to ${r.data.to}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setNotifying(null); }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${data.cases.length} tracked cases? This cannot be undone.`)) return;
    try {
      await api.delete("/compliance/cases");
      toast.success("All cases cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const badge = (c) => {
    if (c.state === "closed") {
      return <span className="inline-flex items-center gap-1.5 rounded-full border bg-stone-100 text-stone-700 border-stone-300 px-2.5 py-1 text-[11px] font-semibold">Closed</span>;
    }
    const days = c.days_remaining;
    const status = c.status;
    const map = {
      active:   { label: `${days} days left`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      expiring: { label: days === 0 ? "Closes today" : `${days} day${days === 1 ? "" : "s"} left`, cls: "bg-amber-50 text-amber-700 border-amber-300" },
      expired:  { label: `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`, cls: "bg-red-50 text-red-700 border-red-300" },
    };
    const m = map[status] || map.active;
    return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${m.cls}`}>{status === "expired" && <AlertTriangle className="w-3 h-3" />}{m.label}</span>;
  };

  return (
    <section data-testid="case-closure-tracker">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-medium text-stone-900">Case closure tracker</h2>
          <p className="mt-1 text-sm text-stone-500">Upload an Excel (.xlsx) or CSV (.csv) of family case numbers and closure dates. We'll flag any case within 30 days of expiring.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="cases-template-btn"
            type="button"
            variant="outline"
            onClick={downloadTemplate}
            className="rounded-xl border-stone-300 text-stone-700"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download template
          </Button>
          <input
            ref={fileRef}
            data-testid="cases-upload-input"
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            onChange={onUpload}
          />
          <Button
            data-testid="cases-upload-btn"
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {busy ? "Importing…" : "Upload Excel or CSV"}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="cases-stat-active" label="Active" value={data.summary.active} tone="emerald" />
        <StatCard testid="cases-stat-expiring" label="Expiring ≤30 days" value={data.summary.expiring} tone="amber" />
        <StatCard testid="cases-stat-expired" label="Expired (kept 90 days)" value={data.summary.expired} tone="red" />
        <StatCard testid="cases-stat-closed" label="Closed" value={data.summary.closed || 0} tone="stone" />
      </div>

      <div className="mt-4 rounded-2xl border border-stone-200 bg-white overflow-x-auto">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr className="text-[11px] uppercase tracking-widest text-stone-500">
              <th className="text-left font-semibold px-5 py-3">Family name</th>
              <th className="text-left font-semibold px-5 py-3">Case #</th>
              <th className="text-left font-semibold px-5 py-3">Closure date</th>
              <th className="text-left font-semibold px-5 py-3">Status</th>
              <th className="text-left font-semibold px-5 py-3">Parent email</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="cases-table-body">
            {loading ? (
              <tr><td colSpan="6" className="px-5 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700 inline" /></td></tr>
            ) : data.cases.length === 0 ? (
              <tr><td colSpan="6" className="px-5 py-12 text-center text-stone-500 text-sm">
                No cases tracked yet. Download the template, fill in your families' case numbers and closure dates, then upload.
              </td></tr>
            ) : data.cases.map((c) => {
              const isClosed = c.state === "closed";
              const days = c.days_remaining;
              const canNotify = !isClosed && !!(c.parent_email) && days <= 14;
              const editing = emailEditing && emailEditing.id === c.id;
              return (
              <tr key={c.id} data-testid={`case-row-${c.id}`} className={`border-b border-stone-100 last:border-0 ${isClosed ? "bg-stone-50/60" : ""}`}>
                <td className={`px-5 py-4 text-sm font-medium ${isClosed ? "text-stone-500 line-through" : "text-stone-900"}`}>{c.family_name}</td>
                <td className={`px-5 py-4 text-sm font-mono ${isClosed ? "text-stone-400" : "text-stone-700"}`}>{c.case_number}</td>
                <td className={`px-5 py-4 text-sm ${isClosed ? "text-stone-400" : "text-stone-700"}`}>{c.closure_date}</td>
                <td className="px-5 py-4">{badge(c)}</td>
                <td className="px-5 py-4 text-sm">
                  {editing ? (
                    <div className="flex items-center gap-1">
                      <input
                        data-testid={`case-email-input-${c.id}`}
                        type="email"
                        autoFocus
                        value={emailEditing.value}
                        placeholder="parent@example.com"
                        onChange={(e) => setEmailEditing({ id: c.id, value: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEmail(); if (e.key === "Escape") setEmailEditing(null); }}
                        className="h-7 px-2 rounded-lg border border-stone-300 text-xs focus:border-emerald-600 outline-none w-56"
                      />
                      <Button data-testid={`case-email-save-${c.id}`} size="sm" onClick={saveEmail} className="h-7 px-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs"><Check className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEmailEditing(null)} className="h-7 px-2 rounded-lg text-xs"><X className="w-3 h-3" /></Button>
                    </div>
                  ) : c.parent_email ? (
                    <button
                      type="button"
                      data-testid={`case-email-edit-${c.id}`}
                      onClick={() => setEmailEditing({ id: c.id, value: c.parent_email })}
                      className={`hover:text-emerald-700 transition-colors text-left ${isClosed ? "text-stone-400" : "text-stone-700"}`}
                      title="Click to edit"
                    >
                      {c.parent_email}
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`case-email-add-${c.id}`}
                      onClick={() => setEmailEditing({ id: c.id, value: "" })}
                      className="text-xs text-stone-400 hover:text-emerald-700 italic"
                    >
                      + add email
                    </button>
                  )}
                  {c.last_notified_at && !editing && (
                    <div className="text-[10px] text-stone-400 mt-0.5">Last notified {new Date(c.last_notified_at).toLocaleDateString()}</div>
                  )}
                </td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  {!isClosed && (
                    <Button
                      data-testid={`case-notify-${c.id}`}
                      onClick={() => notifyFamily(c)}
                      disabled={!canNotify || notifying === c.id}
                      variant="outline"
                      size="sm"
                      className={`rounded-lg h-7 text-xs mr-1 ${canNotify ? "border-amber-300 text-amber-800 hover:bg-amber-50" : "border-stone-200 text-stone-300"}`}
                      title={
                        !c.parent_email ? "Add the parent's email first"
                        : days > 14 ? `Reminder available within 14 days of closure (currently ${days} days away)`
                        : `Email a courtesy reminder to ${c.parent_email}`
                      }
                    >
                      {notifying === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Mail className="w-3 h-3 mr-1" /> Notify family</>}
                    </Button>
                  )}
                  <Button
                    data-testid={`case-toggle-${c.id}`}
                    onClick={() => setCaseState(c, isClosed ? "open" : "closed")}
                    disabled={toggling === c.id}
                    variant="outline"
                    size="sm"
                    className={`rounded-lg h-7 text-xs mr-1 ${isClosed ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-stone-300 text-stone-700 hover:bg-stone-50"}`}
                    title={isClosed ? "Reopen this case" : "Mark this case officially closed"}
                  >
                    {toggling === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : isClosed ? <><FolderOpen className="w-3 h-3 mr-1" /> Reopen</> : <><Check className="w-3 h-3 mr-1" /> Mark closed</>}
                  </Button>
                  <button
                    data-testid={`case-delete-${c.id}`}
                    onClick={() => removeOne(c.id)}
                    className="text-stone-400 hover:text-red-600 transition-colors p-1"
                    title="Remove case"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.cases.length > 0 && (
        <div className="mt-3 flex justify-end">
          <button
            data-testid="cases-clear-all-btn"
            onClick={clearAll}
            className="text-xs text-stone-500 hover:text-red-600 transition-colors"
          >
            Clear all cases
          </button>
        </div>
      )}
    </section>
  );
}

function StatCard({ label, value, tone, testid }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    amber:   "border-amber-300 bg-amber-50/60 text-amber-700",
    red:     "border-red-300 bg-red-50/60 text-red-700",
    stone:   "border-stone-300 bg-stone-100/70 text-stone-600",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="mt-2 font-display text-3xl font-medium">{value}</div>
    </div>
  );
}


// =============== Attendance Archive ===============
// Audit-ready: lists every week that has check-in/out data, with print buttons
// per classroom (DCY 01208).  Directors can upload historical Excel/CSV
// records to backfill the archive — same endpoint Attendance uses for imports.

export function AttendanceArchive() {
  const [weeks, setWeeks] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [zipBusy, setZipBusy] = useState(false);
  const [scanWeek, setScanWeek] = useState(null); // currently-open scan dialog week_of
  const [dragOver, setDragOver] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // {done, total, current}
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/attendance/weeks").then((r) => r.data?.weeks || []),
      api.get("/attendance/scans/recent").then((r) => r.data?.scans || []).catch(() => []),
    ])
      .then(([w, s]) => { setWeeks(w); setRecentScans(s); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const downloadZip = async () => {
    setZipBusy(true);
    try {
      const res = await api.get("/attendance/archive.zip", { responseType: "blob" });
      const objUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const ym = new Date().toISOString().slice(0, 7);
      a.href = objUrl; a.download = `attendance-archive-${ym}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(objUrl), 60_000);
      toast.success("ZIP downloaded.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setZipBusy(false); }
  };

  // Best-effort filename → week-of detection.  Tries common patterns:
  //   "2026-04-20", "20260420", "04-20-2026", "Apr 20 2026", "Week of 2026-04-20"
  // Returns YYYY-MM-DD anchored to the date found (snapped to Monday server-side)
  // or "" if nothing matched (server will default to current Monday).
  const detectWeekFromFilename = (name) => {
    const n = (name || "").toLowerCase();
    const m1 = n.match(/(\d{4})[-_./](\d{1,2})[-_./](\d{1,2})/);
    if (m1) return `${m1[1]}-${m1[2].padStart(2, "0")}-${m1[3].padStart(2, "0")}`;
    const m2 = n.match(/(\d{1,2})[-_./](\d{1,2})[-_./](\d{4})/);
    if (m2) return `${m2[3]}-${m2[1].padStart(2, "0")}-${m2[2].padStart(2, "0")}`;
    const m3 = n.match(/(\d{4})(\d{2})(\d{2})/);
    if (m3) return `${m3[1]}-${m3[2]}-${m3[3]}`;
    return "";
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const detected = detectWeekFromFilename(file.name);
    if (detected) fd.append("week_of", detected);
    // Always store-only: no AI, no auto-import.  Spreadsheets, PDFs and
    // photos all become file artifacts in the archive — the director keeps
    // the original document, period.
    return api.post("/attendance/smart-upload?store_only=1", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
  };

  const handleDroppedFiles = async (filesList) => {
    const files = Array.from(filesList || []).filter((f) => f && f.size > 0);
    if (files.length === 0) return;
    setBusy(true);
    setBatchProgress({ done: 0, total: files.length, current: files[0].name });
    let ok = 0, fail = 0, detectedCount = 0;
    const failures = [];
    let lastScanWeek = null;
    let lastScanFilename = "";
    // Process serially so progress is meaningful; backend is fast enough.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setBatchProgress({ done: i, total: files.length, current: f.name });
      const detected = detectWeekFromFilename(f.name);
      if (detected) detectedCount += 1;
      try {
        const res = await uploadOne(f);
        ok += 1;
        if (res?.data?.kind === "scan") {
          lastScanWeek = res.data.week_of;
          lastScanFilename = res.data.filename;
        }
      } catch (err) {
        fail += 1;
        failures.push(`${f.name}: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setBatchProgress(null);
    setBusy(false);
    load();
    if (ok > 0) {
      toast.success(
        files.length === 1 && lastScanWeek
          ? `Saved "${lastScanFilename}" to week of ${lastScanWeek}.`
          : `Uploaded ${ok} file${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}. ` +
            (detectedCount ? `Detected weeks from ${detectedCount} filename${detectedCount === 1 ? "" : "s"}.` : "Used current week as default."),
        { duration: 8_000 },
      );
    }
    if (fail > 0) {
      console.error("Batch upload failures:", failures);
      toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed — check the browser console.`);
    }
  };

  const onDropZone = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    handleDroppedFiles(e.dataTransfer?.files);
  };

  // Builds a direct GET URL for a scan that carries the user's JWT as
  // `?t=` so a popped-open tab can stream the file (browsers don't send
  // Authorization headers on `window.open`).
  const scanUrl = (id) => {
    const token = (typeof window !== "undefined" && localStorage.getItem("cc_token")) || "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/attendance/scans/${id}?t=${encodeURIComponent(token)}`;
  };
  const downloadScan = (s) => {
    const a = document.createElement("a");
    a.href = scanUrl(s.id); a.download = s.filename || "scan";
    a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const viewScan = (s) => {
    window.open(scanUrl(s.id), "_blank", "noopener,noreferrer");
  };
  const printScan = (s) => {
    const w = window.open(scanUrl(s.id), "_blank", "noopener,noreferrer");
    if (w) {
      try { w.addEventListener("load", () => { try { w.print(); } catch (_e1) { /* PDF viewer blocks programmatic print — that's fine */ } }); }
      catch (_e2) { /* cross-origin guard */ }
    }
  };

  const onUpload = async () => {  // legacy single-file handler, no longer wired
    /* now delegated to handleDroppedFiles for multi-file support */
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/attendance/template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "attendance-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openWeekPdf = async (week_of, classroom = "all") => {
    const key = `${week_of}-${classroom}`;
    setDownloading(key);
    try {
      const url = `/attendance/weekly/pdf?week_of=${week_of}${classroom === "all" ? "" : `&classroom=${encodeURIComponent(classroom)}`}`;
      const res = await api.get(url, { responseType: "blob" });
      const objUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(objUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(objUrl), 60_000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(""); }
  };

  const printWeekBundle = async (week_of) => {
    setDownloading(`${week_of}-bundle`);
    try {
      const res = await api.get(`/attendance/weeks/${week_of}/print.pdf`, { responseType: "blob" });
      const objUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open(objUrl, "_blank", "noopener,noreferrer");
      if (w) {
        try { w.addEventListener("load", () => { try { w.print(); } catch (_e) { /* PDF viewer may block */ } }); }
        catch (_e2) { /* cross-origin guard */ }
      }
      setTimeout(() => window.URL.revokeObjectURL(objUrl), 120_000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(""); }
  };

  const formatWeekLabel = (week_of) => {
    // week_of = Monday of that week. Show "Mon mm/dd – Sun mm/dd, YYYY".
    const mon = new Date(week_of + "T00:00");
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    const opts = { month: "short", day: "numeric" };
    const monStr = mon.toLocaleDateString(undefined, opts);
    const sunStr = sun.toLocaleDateString(undefined, { ...opts, year: "numeric" });
    return `${monStr} – ${sunStr}`;
  };

  return (
    <section data-testid="compliance-attendance-archive">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-700 text-white grid place-items-center">
            <Archive className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-medium text-stone-900">Attendance history</h2>
            <p className="text-sm text-stone-500">Files filed by year · month · week. Upload teacher sheets, print or download anytime — no AI parsing.</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileRef}
            data-testid="compliance-archive-upload-input"
            type="file"
            multiple
            accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp,.heic"
            className="hidden"
            onChange={(e) => { handleDroppedFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
          />
          <Button
            data-testid="compliance-archive-template"
            variant="outline"
            onClick={downloadTemplate}
            className="rounded-xl h-9 text-xs"
          >
            <FileDown className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <Button
            data-testid="compliance-archive-upload"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-9 text-xs"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            Upload attendance (Excel · PDF · photo)
          </Button>
          <Button
            data-testid="compliance-archive-zip"
            disabled={zipBusy || weeks.length === 0}
            onClick={downloadZip}
            className="bg-stone-800 hover:bg-stone-900 text-white rounded-xl h-9 text-xs"
            title="Download every week's DCY 01208 + scans as a single ZIP"
          >
            {zipBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Archive className="w-3.5 h-3.5 mr-1" />}
            Download all as ZIP
          </Button>
        </div>
      </div>

      {/* My uploads queue — recent files across all weeks */}
      {recentScans.length > 0 && (
        <div data-testid="compliance-uploads-queue" className="mb-4 rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-600">Recent uploads</div>
            <div className="text-[10px] text-stone-500">Most recent {recentScans.length}</div>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-stone-100">
            {recentScans.map((s) => (
              <div
                key={s.id}
                data-testid={`compliance-queue-row-${s.id}`}
                className="px-4 py-2 hover:bg-stone-50 flex items-center gap-3 flex-wrap"
              >
                <button
                  type="button"
                  onClick={() => setScanWeek(s.week_of)}
                  className="font-medium text-stone-900 truncate flex-1 min-w-[150px] text-left hover:text-emerald-700"
                  title="Manage all files for this week"
                >
                  {s.filename}
                </button>
                <span className="text-xs text-stone-500">Week of {s.week_of}</span>
                <span className="text-[10px] text-stone-400 hidden sm:inline">{(s.size / 1024).toFixed(0)} KB</span>
                <Button
                  data-testid={`compliance-queue-view-${s.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => viewScan(s)}
                  className="rounded-lg h-7 text-xs border-stone-300"
                  title="Open in a new tab"
                >
                  <Eye className="w-3 h-3 mr-1" /> View
                </Button>
                <Button
                  data-testid={`compliance-queue-print-${s.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => printScan(s)}
                  className="rounded-lg h-7 text-xs border-stone-300"
                  title="Open in a new tab to print"
                >
                  <Printer className="w-3 h-3 mr-1" /> Print
                </Button>
                <Button
                  data-testid={`compliance-queue-download-${s.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => downloadScan(s)}
                  className="rounded-lg h-7 text-xs border-stone-300"
                  title="Save a copy to disk"
                >
                  <FileDown className="w-3 h-3 mr-1" /> Download
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drag-and-drop bulk zone — pick a folder of PDFs and we'll auto-detect
          each file's week from its name (or default to current week). */}
      <div
        data-testid="compliance-archive-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDropZone}
        onClick={() => !busy && fileRef.current?.click()}
        className={`mb-4 cursor-pointer rounded-2xl border-2 border-dashed p-5 text-center transition-colors ${
          dragOver ? "border-emerald-500 bg-emerald-50/70" : "border-stone-300 bg-stone-50/40 hover:border-emerald-400 hover:bg-emerald-50/30"
        } ${busy ? "opacity-60 pointer-events-none" : ""}`}
      >
        <Archive className="w-7 h-7 mx-auto text-emerald-700" />
        <p className="mt-2 text-sm font-medium text-stone-800">
          Drop PDFs, photos or Excel sheets here
        </p>
        <p className="text-xs text-stone-600">
          We&apos;ll file each one against its week. Names like <code className="bg-stone-100 px-1 rounded">Week-of-2026-04-20.pdf</code> auto-route; anything else lands in this week.
        </p>
        {batchProgress && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-800 bg-white border border-emerald-200 rounded-full px-3 py-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Uploading {batchProgress.done + 1} of {batchProgress.total} · {batchProgress.current}
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
      ) : weeks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 p-8 text-center">
          <CalendarRange className="w-8 h-8 mx-auto text-stone-400" />
          <p className="mt-3 text-sm text-stone-600">No attendance recorded yet.</p>
          <p className="text-xs text-stone-500 mt-1">Drop a folder of PDFs / photos / Excels above, or pick a Monday below to start a new week slot.</p>
        </div>
      ) : (
        <ArchiveTree
          weeks={weeks}
          downloading={downloading}
          onOpenScans={(wk) => setScanWeek(wk)}
          onOpenAllPdf={(wk) => openWeekPdf(wk, "all")}
          onPrintBundle={(wk) => printWeekBundle(wk)}
          formatWeekLabel={formatWeekLabel}
        />
      )}

      {weeks.length > 0 && (
        <p className="mt-3 text-[11px] text-stone-500 italic">
          Each PDF is a DCY 01208 (Ohio attendance log) with all four classrooms on separate pages. Auditors can hand-sign and file directly.
        </p>
      )}

      {/* Quick "create a week" button — for archiving older handwritten sheets
          whose events were never typed into the digital system. */}
      <div className="mt-4 rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/30 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-stone-700">
          <strong className="text-emerald-800">Have older paper attendance to archive?</strong>{" "}
          Pick any Monday — we&apos;ll create a slot and let you attach the scanned/photo sheets to it.
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="archive-pick-week"
            type="date"
            onChange={(e) => { if (e.target.value) setScanWeek(e.target.value); }}
            className="h-9 px-2 text-xs rounded-lg border border-stone-300 focus:border-emerald-600 outline-none"
          />
          <span className="text-[11px] text-stone-500">Any day in that week → we snap to its Monday.</span>
        </div>
      </div>

      {scanWeek && (
        <ScanManagerDialog
          weekOf={scanWeek}
          onClose={() => setScanWeek(null)}
          onChange={() => load()}
        />
      )}
    </section>
  );
}

// ----- Year → Month → Week tree for the Attendance archive -----
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function ArchiveTree({ weeks, downloading, onOpenScans, onOpenAllPdf, onPrintBundle, formatWeekLabel }) {
  // Bucket every week into Year → Month using the Monday date string.
  const grouped = useMemo(() => {
    const byYear = new Map();
    for (const w of weeks) {
      const [y, m] = (w.week_of || "").split("-");
      if (!y || !m) continue;
      if (!byYear.has(y)) byYear.set(y, new Map());
      const mm = byYear.get(y);
      if (!mm.has(m)) mm.set(m, []);
      mm.get(m).push(w);
    }
    // Sort: years desc, months desc, weeks desc.
    return Array.from(byYear.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([y, mm]) => ({
        year: y,
        months: Array.from(mm.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([m, ws]) => ({
            month: m,
            label: MONTH_NAMES[parseInt(m, 10) - 1] || m,
            weeks: ws.slice().sort((a, b) => b.week_of.localeCompare(a.week_of)),
            events: ws.reduce((s, w) => s + (w.events || 0), 0),
            scans: ws.reduce((s, w) => s + (w.scans || 0), 0),
            children: new Set(ws.flatMap((w) => w.classrooms || [])).size,
          })),
      }));
  }, [weeks]);

  // Default-open: most recent year + most recent month inside it.
  const initialOpen = useMemo(() => {
    const o = { years: {}, months: {} };
    if (grouped.length > 0) {
      o.years[grouped[0].year] = true;
      if (grouped[0].months.length > 0) {
        o.months[`${grouped[0].year}-${grouped[0].months[0].month}`] = true;
      }
    }
    return o;
  }, [grouped]);
  const [openYears, setOpenYears] = useState(initialOpen.years);
  const [openMonths, setOpenMonths] = useState(initialOpen.months);
  useEffect(() => { setOpenYears(initialOpen.years); setOpenMonths(initialOpen.months); }, [initialOpen]);

  const toggleYear = (y) => setOpenYears((o) => ({ ...o, [y]: !o[y] }));
  const toggleMonth = (key) => setOpenMonths((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div data-testid="compliance-archive-tree" className="space-y-3">
      {grouped.map(({ year, months }) => {
        const yearEvents = months.reduce((s, m) => s + m.events, 0);
        const yearScans = months.reduce((s, m) => s + m.scans, 0);
        const yOpen = !!openYears[year];
        return (
          <div key={year} data-testid={`archive-year-${year}`} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <button
              type="button"
              data-testid={`archive-year-toggle-${year}`}
              onClick={() => toggleYear(year)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 bg-stone-900 text-white hover:bg-stone-800 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                {yOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-display text-lg font-medium">{year}</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-stone-300">
                {months.length} month{months.length === 1 ? "" : "s"} · {yearEvents} events · {yearScans} scan{yearScans === 1 ? "" : "s"}
              </div>
            </button>
            {yOpen && (
              <div className="divide-y divide-stone-100">
                {months.map((m) => {
                  const mKey = `${year}-${m.month}`;
                  const mOpen = !!openMonths[mKey];
                  return (
                    <div key={mKey} data-testid={`archive-month-${mKey}`}>
                      <button
                        type="button"
                        data-testid={`archive-month-toggle-${mKey}`}
                        onClick={() => toggleMonth(mKey)}
                        className="w-full flex items-center justify-between gap-3 px-5 py-2.5 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          {mOpen ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronRight className="w-4 h-4 text-stone-500" />}
                          <span className="font-medium text-stone-900">{m.label}</span>
                        </div>
                        <div className="text-[11px] uppercase tracking-widest text-stone-500">
                          {m.weeks.length} week{m.weeks.length === 1 ? "" : "s"} · {m.events} events · {m.scans} scan{m.scans === 1 ? "" : "s"}
                        </div>
                      </button>
                      {mOpen && (
                        <table className="w-full text-sm">
                          <thead className="bg-white text-[10px] uppercase tracking-widest text-stone-400">
                            <tr>
                              <th className="text-left pl-12 pr-5 py-2 font-normal">Week</th>
                              <th className="text-right px-5 py-2 font-normal">Events</th>
                              <th className="text-left px-5 py-2 font-normal">Classrooms</th>
                              <th className="text-right px-5 py-2 font-normal">Scans</th>
                              <th className="text-right px-5 py-2 font-normal"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.weeks.map((w) => (
                              <tr key={w.week_of} data-testid={`archive-week-${w.week_of}`} className="border-t border-stone-100 hover:bg-emerald-50/20">
                                <td className="pl-12 pr-5 py-3">
                                  <div className="font-medium text-stone-900">{formatWeekLabel(w.week_of)}</div>
                                  <div className="text-[10px] text-stone-500 mt-0.5">Week of {w.week_of}</div>
                                </td>
                                <td className="px-5 py-3 text-right font-display text-stone-900">
                                  {w.events}
                                  <div className="text-[10px] text-stone-500">{w.check_ins} in · {w.check_outs} out</div>
                                </td>
                                <td className="px-5 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {(w.classrooms || []).map((c) => (
                                      <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wider">{c}</span>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-5 py-3 text-right">
                                  {(w.scans || 0) > 0 ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-wider">
                                      {w.scans} file{w.scans === 1 ? "" : "s"}
                                    </span>
                                  ) : <span className="text-[10px] text-stone-400">—</span>}
                                </td>
                                <td className="px-5 py-3 text-right whitespace-nowrap">
                                  <Button
                                    data-testid={`archive-scans-${w.week_of}`}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onOpenScans(w.week_of)}
                                    className="rounded-lg h-7 text-xs mr-1 border-stone-300"
                                    title="View, add or manage uploaded handwritten sheets"
                                  >
                                    <ImageIcon className="w-3 h-3 mr-1" />
                                    {w.scans > 0 ? `${w.scans} scan${w.scans === 1 ? "" : "s"}` : "Add scans"}
                                  </Button>
                                  <Button
                                    data-testid={`archive-print-all-${w.week_of}`}
                                    size="sm"
                                    disabled={!!downloading || w.events === 0}
                                    onClick={() => onOpenAllPdf(w.week_of)}
                                    className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-7 text-xs mr-1"
                                    title="Open the digital DCY 01208 (all classrooms)"
                                  >
                                    {downloading === `${w.week_of}-all` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3 mr-1" />}
                                    DCY 01208
                                  </Button>
                                  <Button
                                    data-testid={`archive-print-week-${w.week_of}`}
                                    size="sm"
                                    variant="outline"
                                    disabled={!!downloading || (w.events === 0 && (w.scans || 0) === 0)}
                                    onClick={() => onPrintBundle(w.week_of)}
                                    className="rounded-lg h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                                    title="DCY 01208 + every uploaded scan, merged into one printable PDF"
                                  >
                                    {downloading === `${w.week_of}-bundle` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3 mr-1" />}
                                    Print this week
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}



function ScanManagerDialog({ weekOf, onClose, onChange }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null); // {scan_id, mode: 'rename'|'move', value}
  const [saving, setSaving] = useState(false);
  const scanFileRef = useRef(null);

  const scanUrl = (id) => {
    const token = (typeof window !== "undefined" && localStorage.getItem("cc_token")) || "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/attendance/scans/${id}?t=${encodeURIComponent(token)}`;
  };

  const fetchScans = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/attendance/weeks/${weekOf}/scans`);
      setScans(r.data?.scans || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchScans(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [weekOf]);

  const uploadScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/attendance/weeks/${weekOf}/scans`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached.");
      await fetchScans();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (scanFileRef.current) scanFileRef.current.value = ""; }
  };

  const removeScan = async (s) => {
    if (!window.confirm(`Permanently delete "${s.filename}" from week of ${weekOf}?`)) return;
    try {
      await api.delete(`/attendance/scans/${s.id}`);
      toast.success("File deleted.");
      await fetchScans();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const viewScan = (s) => { window.open(scanUrl(s.id), "_blank", "noopener,noreferrer"); };
  const downloadScan = (s) => {
    const a = document.createElement("a");
    a.href = scanUrl(s.id); a.download = s.filename || "scan";
    a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const printScan = (s) => {
    const w = window.open(scanUrl(s.id), "_blank", "noopener,noreferrer");
    if (w) {
      try { w.addEventListener("load", () => { try { w.print(); } catch (_e1) { /* PDF viewer blocks programmatic print */ } }); }
      catch (_e2) { /* cross-origin guard */ }
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      if (editing.mode === "rename") payload.filename = editing.value;
      if (editing.mode === "move") payload.week_of = editing.value;
      await api.patch(`/attendance/scans/${editing.scan_id}`, payload);
      toast.success(editing.mode === "rename" ? "File renamed." : "File moved to another week.");
      setEditing(null);
      await fetchScans();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="archive-scan-dialog" className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Attendance files · Week of {weekOf}</DialogTitle>
          <DialogDescription>
            Upload PDFs, photos or Excel sheets of teachers&apos; signed attendance. Files are filed against this week — no AI, no auto-import.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-dashed border-emerald-300 p-4 text-center">
          <input
            ref={scanFileRef}
            data-testid="archive-scan-file"
            type="file"
            accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.webp,.heic"
            className="hidden"
            onChange={uploadScan}
          />
          <ImageIcon className="w-8 h-8 mx-auto text-stone-400" />
          <p className="mt-2 text-sm text-stone-700">Attach a PDF, photo (JPG/PNG/HEIC) or spreadsheet (XLSX/CSV).</p>
          <Button
            data-testid="archive-scan-upload-btn"
            disabled={uploading}
            onClick={() => scanFileRef.current?.click()}
            className="mt-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-9 text-xs"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
            Choose file
          </Button>
        </div>

        {loading ? (
          <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
        ) : scans.length === 0 ? (
          <div className="text-center text-sm text-stone-500 italic py-6">No files attached to this week yet.</div>
        ) : (
          <div className="rounded-xl border border-stone-200 overflow-hidden mt-3">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left px-3 py-2">Uploaded</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {scans.map((s) => (
                  <tr key={s.id} data-testid={`archive-scan-row-${s.id}`} className="border-t border-stone-100">
                    <td className="px-3 py-2">
                      {editing && editing.scan_id === s.id && editing.mode === "rename" ? (
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`archive-scan-rename-input-${s.id}`}
                            value={editing.value}
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            className="flex-1 h-8 px-2 rounded-lg border border-stone-300 text-sm focus:border-emerald-600 outline-none"
                            autoFocus
                          />
                          <Button data-testid={`archive-scan-rename-save-${s.id}`} size="sm" disabled={saving} onClick={saveEdit} className="h-7 px-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs">
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-7 px-2 rounded-lg text-xs">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : editing && editing.scan_id === s.id && editing.mode === "move" ? (
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`archive-scan-move-input-${s.id}`}
                            type="date"
                            value={editing.value}
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            className="h-8 px-2 rounded-lg border border-stone-300 text-sm focus:border-emerald-600 outline-none"
                            autoFocus
                          />
                          <span className="text-[10px] text-stone-500">→ snaps to Monday</span>
                          <Button data-testid={`archive-scan-move-save-${s.id}`} size="sm" disabled={saving || !editing.value} onClick={saveEdit} className="h-7 px-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs">
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="h-7 px-2 rounded-lg text-xs">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="font-medium text-stone-900 truncate max-w-[320px]">{s.filename}</div>
                          <div className="text-[10px] text-stone-500">{s.content_type} · {(s.size / 1024).toFixed(1)} KB</div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-stone-500 text-xs whitespace-nowrap">{new Date(s.uploaded_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button data-testid={`archive-scan-view-${s.id}`} variant="outline" size="sm" onClick={() => viewScan(s)} className="rounded-lg h-7 text-xs mr-1 border-stone-300" title="Open in a new tab">
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <Button data-testid={`archive-scan-print-${s.id}`} variant="outline" size="sm" onClick={() => printScan(s)} className="rounded-lg h-7 text-xs mr-1 border-stone-300" title="Open & try to print">
                        <Printer className="w-3 h-3 mr-1" /> Print
                      </Button>
                      <Button data-testid={`archive-scan-download-${s.id}`} variant="outline" size="sm" onClick={() => downloadScan(s)} className="rounded-lg h-7 text-xs mr-1 border-stone-300" title="Save a copy to disk">
                        <FileDown className="w-3 h-3 mr-1" /> Download
                      </Button>
                      <Button data-testid={`archive-scan-rename-${s.id}`} variant="outline" size="sm" onClick={() => setEditing({ scan_id: s.id, mode: "rename", value: s.filename })} className="rounded-lg h-7 text-xs mr-1 border-stone-300" title="Rename file">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button data-testid={`archive-scan-move-${s.id}`} variant="outline" size="sm" onClick={() => setEditing({ scan_id: s.id, mode: "move", value: weekOf })} className="rounded-lg h-7 text-xs mr-1 border-stone-300" title="Move to another week">
                        <Move className="w-3 h-3" />
                      </Button>
                      <Button data-testid={`archive-scan-delete-${s.id}`} variant="outline" size="sm" onClick={() => removeScan(s)} className="rounded-lg h-7 text-xs border-red-300 text-red-700 hover:bg-red-50" title="Delete">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MonthlyDigestActions() {
  const [busyDl, setBusyDl] = useState(false);
  const [busyEmail, setBusyEmail] = useState(false);
  const downloadDigest = async () => {
    setBusyDl(true);
    try {
      const res = await api.get("/compliance/monthly-digest.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const tag = new Date().toISOString().slice(0, 7);
      a.href = url; a.download = `staff-compliance-digest-${tag}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyDl(false); }
  };
  const emailDigest = async () => {
    if (!window.confirm("Email the monthly compliance digest PDF to yourself now?")) return;
    setBusyEmail(true);
    try {
      const res = await api.post("/compliance/monthly-digest/email", {});
      if (res.data?.sent) toast.success(`Digest emailed to ${res.data.to}`);
      else toast.warning(`Email not sent: ${res.data?.reason || "no provider"}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyEmail(false); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="monthly-digest-actions">
      <Button
        type="button"
        data-testid="monthly-digest-download"
        onClick={downloadDigest}
        disabled={busyDl}
        variant="outline"
        className="rounded-xl"
      >
        {busyDl ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
        Download monthly digest
      </Button>
      <Button
        type="button"
        data-testid="monthly-digest-email"
        onClick={emailDigest}
        disabled={busyEmail}
        className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
      >
        {busyEmail ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
        Email it to me
      </Button>
    </div>
  );
}

// ============ Tiny banner pointing at the new Center Settings page ============
// The director's name + standard hours card lived here in iter-68/68b/68c. As
// of iter-69 those settings (plus tuition rates, contact info, and a logo
// upload) moved to a dedicated /dashboard/settings page so the surface stays
// tidy when other centers join. Show a one-line nudge so directors still
// remember WHERE the DCY 01229 defaults live.
function SettingsHintBanner() {
  return (
    <Link
      to="/dashboard/settings"
      data-testid="compliance-settings-hint"
      className="group block rounded-2xl border border-emerald-200 bg-emerald-50/60 px-5 py-3 hover:bg-emerald-50 transition-colors"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-stone-700">
          <span className="font-semibold text-emerald-800">DCY 01229 defaults</span>
          <span className="text-stone-500"> · director&apos;s name, standard hours, and tuition rates now live on </span>
          <span className="font-semibold text-emerald-800">Center Settings</span>
        </div>
        <ChevronRight className="w-4 h-4 text-emerald-700 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}


